import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useSessionUser } from "@/hooks/useSessionUser";
import { createBillingCheckout, createBillingPortal, syncBillingSeatLimit } from "@/api/billing";
import { hasActiveSubscription as sessionHasActiveSubscription } from "@/lib/billingGate";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

export default function BillingSettingsPage() {
  const { isOnTrial, trialExpired, trialDaysLeft, trialEndsAt, tier, session, refreshSession } = useSessionUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnNotice, setReturnNotice] = useState<string | null>(null);
  const handledReturnRef = useRef(false);

  const hasActiveSubscription = sessionHasActiveSubscription(session as Record<string, unknown> | null);

  // Returning from Stripe Checkout: sync the subscription once, then refresh
  // entitlements so paid access applies without signing out.
  useEffect(() => {
    const outcome = searchParams.get("billing");
    if (!outcome || handledReturnRef.current) return;
    handledReturnRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete("billing");
    setSearchParams(next, { replace: true });
    if (outcome === "cancel") {
      setReturnNotice("Checkout was cancelled. You haven't been charged.");
      return;
    }
    if (outcome !== "success") return;
    setReturnNotice("Thanks! Activating your plan…");
    syncBillingSeatLimit({})
      .catch(() => undefined)
      .then(() => refreshSession?.())
      .then(() => setReturnNotice("Thanks! Your plan is active."))
      .catch(() =>
        setReturnNotice("Payment received. If your plan doesn't show as active in a minute, refresh this page."),
      );
  }, [refreshSession, searchParams, setSearchParams]);

  async function handleSubscribe() {
    setLoading(true);
    setError(null);
    try {
      const base = window.location.origin;
      const result = await createBillingCheckout({
        tier: "PRO",
        success_url: `${base}/settings/billing?billing=success`,
        cancel_url: `${base}/settings/billing?billing=cancel`,
      });
      window.location.href = result.checkout_url;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  async function handleManage() {
    setLoading(true);
    setError(null);
    try {
      const result = await createBillingPortal();
      window.location.href = result.portal_url;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return (
    <div className="aa-page-shell space-y-6">
      <PageHeader title="Billing" description="Manage your subscription, plan, and payment details." />
      {returnNotice && (
        <p role="status" className="aa-glass-card rounded-lg px-4 py-3 text-sm text-foreground">
          {returnNotice}
        </p>
      )}

      {/* Trial active */}
      {isOnTrial && (
        <div className="aa-glass-card rounded-lg p-6 border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-base font-semibold text-foreground flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                30-Day Free Trial Active
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                You have full PRO access.{" "}
                {trialDaysLeft !== null && trialDaysLeft > 0
                  ? `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} remaining`
                  : "Trial ends today"
                }
                {trialEndsAt ? ` — expires ${formatDate(trialEndsAt)}.` : "."}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Subscribe before your trial ends to keep uninterrupted access.
              </p>
            </div>
            <Button
              onClick={handleSubscribe}
              disabled={loading}
              className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white"
            >
              {loading ? "Redirecting…" : "Subscribe to continue →"}
            </Button>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </div>
      )}

      {/* Trial expired, no subscription */}
      {trialExpired && !hasActiveSubscription && (
        <div className="aa-glass-card rounded-lg p-6 border border-destructive/30 bg-destructive/5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-base font-semibold text-foreground">Your trial has ended</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Subscribe to restore full access to all PRO features.
              </p>
            </div>
            <Button onClick={handleSubscribe} disabled={loading} variant="destructive" className="shrink-0">
              {loading ? "Redirecting…" : "Choose a plan →"}
            </Button>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </div>
      )}

      {/* Active subscription */}
      {hasActiveSubscription && (
        <div className="aa-glass-card rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-base font-semibold text-foreground flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                {tier ?? "PRO"} — Active Subscription
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Your subscription is active. Manage payment, seats, and invoices below.
              </p>
            </div>
            <Button onClick={handleManage} disabled={loading} variant="outline" className="shrink-0">
              {loading ? "Redirecting…" : "Manage billing →"}
            </Button>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </div>
      )}

      {/* No trial, no subscription */}
      {!isOnTrial && !trialExpired && !hasActiveSubscription && (
        <div className="aa-glass-card rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-base font-semibold text-foreground">Upgrade your plan</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Subscribe to keep discovering venues, saving opportunities, and managing your booking pipeline.
              </p>
            </div>
            <Button onClick={handleSubscribe} disabled={loading} className="shrink-0">
              {loading ? "Redirecting…" : "Subscribe →"}
            </Button>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
}
